import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import "./IndustriesMaster.css";
import {usePermission} from "../../hooks/usePermissions";
import { formatAdminDateTime } from "../../utils/datetime";



const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const EditIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const DeleteModal = ({ item, onClose, onConfirm, submitting }) => (
  <div
    className="im-modal-overlay"
  >
    <div className="im-modal im-modal-max-400">
      <div className="im-modal-header">
        <h2>Delete Industry</h2>
        <button
          type="button"
          className="im-modal-close"
          onClick={onClose}
          disabled={submitting}
        >
          <CloseIcon />
        </button>
      </div>
      <div className="im-modal-body im-modal-body-centered">
        <div className="im-confirm-icon">Delete</div>
        <p className="im-confirm-msg">Are you sure you want to delete</p>
        <p className="im-confirm-name">"{item.industry_name}"?</p>
        <p className="im-error-text">
          This action cannot be undone.
        </p>
      </div>
      <div className="im-modal-footer im-justify-center">
        <button
          type="button"
          className="im-btn-cancel"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="im-btn-delete"
          onClick={() => onConfirm(item.id)}
          disabled={submitting}
        >
          {submitting ? "Deleting..." : "Yes, Delete"}
        </button>
      </div>
    </div>
  </div>
);

const AddModal = ({ initialData, mode, onClose, onSave, submitting }) => {
  const [form, setForm] = useState({
    industry_code: initialData?.industry_code ?? "",
    industry_name: initialData?.industry_name ?? "",
    brief_description: initialData?.brief_description ?? "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      industry_code: Number(form.industry_code),
      industry_name: form.industry_name.trim(),
      brief_description: form.brief_description.trim(),
    });
  };

  return (
    <div
      className="im-modal-overlay"
    >
      <div className="im-modal">
        <div className="im-modal-header">
          <h2>{mode === "edit" ? "Edit Industry" : "Add New Industry"}</h2>
          <button
            type="button"
            className="im-modal-close"
            onClick={onClose}
            disabled={submitting}
          >
            <CloseIcon />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="im-modal-body">
            <div className="im-form-row">
              <div className="im-form-group">
                <label>
                  Industry Code <span className="required">*</span>
                </label>
                <input
                  className="im-input"
                  type="number"
                  required
                  value={form.industry_code}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, industry_code: e.target.value }))
                  }
                  placeholder="e.g. 100"
                />
              </div>
              <div className="im-form-group">
                <label>
                  Industry Name <span className="required">*</span>
                </label>
                <input
                  className="im-input"
                  type="text"
                  required
                  value={form.industry_name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, industry_name: e.target.value }))
                  }
                  placeholder="e.g. IT"
                />
              </div>
            </div>
            <div className="im-form-row">
              <div className="im-form-group full">
                <label>Brief Description</label>
                <textarea
                  className="im-textarea"
                  value={form.brief_description}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      brief_description: e.target.value,
                    }))
                  }
                  placeholder="Enter description..."
                />
              </div>
            </div>
          </div>
          <div className="im-modal-footer">
            <button
              type="button"
              className="im-btn-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="im-btn-save" disabled={submitting}>
              {submitting
                ? mode === "edit"
                  ? "Updating..."
                  : "Saving..."
                : mode === "edit"
                  ? "Update Industry"
                  : "Save Industry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const IndustriesMaster = () => {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [descriptionFilter, setDescriptionFilter] = useState("all");
  const [sortKey, setSortKey] = useState("industry_code");
  const [sortDir, setSortDir] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalMode, setModalMode] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const {hasPermission} = usePermission();


  const itemsPerPage = 8;

  const fetchIndustries = async () => {
    try {
      setLoading(true);
      const response = await api.get("/tagindustry", {
        params: {
          search: appliedSearch || undefined,
          description_filter:
            descriptionFilter !== "all" ? descriptionFilter : undefined,
          sort_by: sortKey,
          sort_dir: sortDir,
        },
      });
      setIndustries(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      toast.error(error?.message || "Failed to load industries");
      setIndustries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIndustries();
  }, [appliedSearch, descriptionFilter, sortKey, sortDir]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch, descriptionFilter, sortKey, sortDir]);

  const filtered = useMemo(() => industries, [industries]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginatedIndustries = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setSortKey(key);
    setSortDir(key === "industry_name" || key === "brief_description" ? "asc" : "desc");
  };

  const sortIcon = (key) =>
    sortKey === key ? (sortDir === "asc" ? "▲" : "▼") : "⇅";

  const handleSearch = () => {
    setAppliedSearch(searchQuery);
  };

  const handleSearchKey = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleDeletePrompt = (industry) => {
    setSelectedIndustry(industry);
    setModalMode("delete");
  };

  const handleEditPrompt = (industry) => {
    setSelectedIndustry(industry);
    setModalMode("edit");
  };

  const handleSaveAdd = async (payload) => {
    if (!payload.industry_code || !payload.industry_name) {
      toast.error("Industry code and industry name are required");
      return;
    }

    try {
      setSaving(true);
      const response = await api.post("/tagindustry", payload);
      setIndustries((prev) => [response.data, ...prev]);
      setModalMode(null);
      toast.success("Industry added successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to add industry");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (payload) => {
    if (!selectedIndustry?.id) return;
    if (!payload.industry_code || !payload.industry_name) {
      toast.error("Industry code and industry name are required");
      return;
    }

    try {
      setSaving(true);
      const response = await api.put(`/tagindustry/${selectedIndustry.id}`, payload);
      setIndustries((prev) =>
        prev.map((item) => (item.id === selectedIndustry.id ? response.data : item))
      );
      setModalMode(null);
      setSelectedIndustry(null);
      toast.success("Industry updated successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to update industry");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async (id) => {
    try {
      setDeleting(true);
      await api.delete(`/tagindustry/${id}`);
      setIndustries((prev) => prev.filter((item) => item.id !== id));
      setModalMode(null);
      setSelectedIndustry(null);
      toast.success("Industry deleted successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to delete industry");
    } finally {
      setDeleting(false);
    }
  };

  const SkeletonRows = () => (
    <>
      {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
        <tr key={i} className="im-skeleton-row">
          <td><span className="im-skel im-w-40" /></td>
          <td><span className="im-skel im-w-80" /></td>
          <td><span className="im-skel im-w-140" /></td>
          <td><span className="im-skel im-w-200" /></td>
          <td><span className="im-skel im-w-52" /></td>
        </tr>
      ))}
    </>
  );

  const ThCol = ({ col, label, center }) => (
    <th
      className={`sortable${center ? " center" : ""}`}
      onClick={() => handleSort(col)}
      title={`Sort by ${label}`}
    >
      <span className="im-th-inner">
        {label}
        <span className="im-sort-icon">{sortIcon(col)}</span>
      </span>
    </th>
  );

  return (
    <div className="im-container">
      <div className="im-page-header">
        <div className="im-header-left">
          <h1 className="im-page-title">Industry Master</h1>
          <p className="im-page-subtitle">{filtered.length} industry{filtered.length !== 1 ? "s" : ""} found</p>
        </div>
        <div className="im-header-right">
          {hasPermission("CREATE_INDUSTRY_MASTER") && (
            <button
              type="button"
              className="im-btn-add"
              onClick={() => {
                setSelectedIndustry(null);
                setModalMode("add");
              }}
            >
              + Add New Industry
            </button>
          )}
        </div>
      </div>

      <div className="im-table-filters">
        <div className="im-filters-right">
          <select
            className="im-filter-select"
            value={descriptionFilter}
            onChange={(e) => setDescriptionFilter(e.target.value)}
          >
            <option value="all">All Descriptions</option>
            <option value="with_description">With Description</option>
            <option value="without_description">Without Description</option>
          </select>
          <div className="im-search-wrap">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search by code, name, description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKey}
            />
          </div>
          <button type="button" className="im-btn-search" onClick={handleSearch}>
            Search
          </button>
        </div>
      </div>

      <div className="im-table-wrap">
        <table className="im-table">
          <thead>
            <tr>
              <ThCol col="id" label="ID" />
              <ThCol col="industry_code" label="Industry Code" />
              <ThCol col="industry_name" label="Industry Name" />
              <ThCol col="brief_description" label="Brief Description" />
              <th className="im-text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : paginatedIndustries.length === 0 ? (
              <tr className="im-empty-row">
                <td colSpan={5}>
                  <div className="im-empty-icon">No Data</div>
                  <h3>No industries found</h3>
                  <p>Try adjusting your search query.</p>
                </td>
              </tr>
            ) : (
              paginatedIndustries.map((industry) => (
                <tr key={industry.id}>
                  <td><span className="im-cell-id">#{industry.id}</span></td>
                  <td>
                    <span className="im-cell-code">
                      {industry.industry_code}
                    </span>
                  </td>
                  <td><div className="im-cell-name">{industry.industry_name}</div></td>
                  <td>
                    <span className={`im-cell-desc ${!industry.brief_description ? "im-italic" : ""}`}>
                      {industry.brief_description || "No description provided"}
                    </span>
                  </td>
                  <td className="im-actions-cell">
                    <div className="im-actions-wrapper">
                    {hasPermission("MODIFY_INDUSTRY_MASTER") && ( 
                      <button
                        type="button"
                        className="im-icon-btn"
                        title="Edit"
                        onClick={() => handleEditPrompt(industry)}
                      >
                        <EditIcon />
                      </button>
                    )}
                    {hasPermission("DELETE_INDUSTRY_MASTER") && ( 
                      <button
                        type="button"
                        className="im-icon-btn delete"
                        title="Delete"
                        onClick={() => handleDeletePrompt(industry)}
                      >
                        <TrashIcon />
                      </button>
                    )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {!loading && filtered.length > 0 && (
          <div className="im-table-footer">
            <span>Showing {paginatedIndustries.length} of {filtered.length} industries</span>

            {totalPages > 1 && (
              <div className="im-pagination">
                <button
                  type="button"
                  className="im-page-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                >
                  {"<"}
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        type="button"
                        key={page}
                        className={`im-page-btn ${currentPage === page ? "active" : ""}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    );
                  }

                  if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span key={page} className="im-pagination-ellipsis">
                        ...
                      </span>
                    );
                  }

                  return null;
                })}

                <button
                  type="button"
                  className="im-page-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                >
                  {">"}
                </button>
              </div>
            )}

            <span className="im-pagination-count">
              Last refreshed: {formatAdminDateTime(new Date())}
            </span>
          </div>
        )}
      </div>

      {modalMode === "delete" && selectedIndustry && (
        <DeleteModal
          item={selectedIndustry}
          onClose={() => {
            if (!deleting) {
              setModalMode(null);
              setSelectedIndustry(null);
            }
          }}
          onConfirm={handleDeleteConfirm}
          submitting={deleting}
        />
      )}

      {(modalMode === "add" || modalMode === "edit") && (
        <AddModal
          initialData={selectedIndustry}
          mode={modalMode}
          onClose={() => {
            if (!saving) {
              setModalMode(null);
              setSelectedIndustry(null);
            }
          }}
          onSave={modalMode === "edit" ? handleUpdate : handleSaveAdd}
          submitting={saving}
        />
      )}
    </div>
  );
};

export default IndustriesMaster;
